import { Divider, Flex, Group, Select } from '@byecode/ui'
import { FlowSelectSourceType } from '@lighthouse/core'
import {
    type FlowNode,
    getFieldOptions,
    getPrimaryDsSystemFieldId,
    getUpstreamRealDsId,
    OtherPlatformNodeType,
    type WeChatRefundActionConfig
} from '@lighthouse/shared'
import React, { useMemo } from 'react'

import { FieldSelect } from '@/components/FieldSelect'
import { PopoverQuestion } from '@/components/PopoverQuestion'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSource } from '@/hooks/useDataSource'

import OutputVariable from '../../../OutputVariable'
import * as SC from './styles'

interface RefundProps {
    allParentNodes?: FlowNode[]
    value: WeChatRefundActionConfig
    onChange: (value: WeChatRefundActionConfig) => void
}

const EmptyArray: FlowNode[] = []

const Refund: React.FunctionComponent<RefundProps> = props => {
    const { allParentNodes = EmptyArray, value, onChange } = props

    const { type } = useFlowContainerContext()
    const isWorkflow = Boolean(type)
    const { nodeId, dataSourceId: dsId, selectType, payOrderFieldId, payAmountFieldId, refundAmountFieldId, reasonFieldId } = value

    const dataSourceId = useMemo(() => {
        if (selectType === FlowSelectSourceType.UPSTREAM) {
            return getUpstreamRealDsId(nodeId, allParentNodes)
        }
        return dsId
    }, [allParentNodes, dsId, nodeId, selectType])

    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSource = useDataSource(appId, envId, dataSourceId ?? '')

    const { schema, viewOptions, type: dsType } = dataSource || {}
    const { tableProps = [] } = viewOptions || {}
    const lookTextFieldOptions = useMemo(() => {
        if (!schema) {
            return []
        }
        return getFieldOptions(schema, tableProps, field => field.innerType === 'TEXT')
    }, [schema, tableProps])

    const lookNumberFieldOptions = useMemo(() => {
        if (!schema) {
            return []
        }
        return getFieldOptions(schema, tableProps, field => field.innerType === 'NUMBER')
    }, [schema, tableProps])

    const primaryDsSystemFieldId = useMemo(() => {
        if (!schema || !viewOptions || !dsType) {
            return
        }
        return getPrimaryDsSystemFieldId('ID', schema, viewOptions, dsType)
    }, [schema, viewOptions, dsType])

    if (!dataSource) {
        return null
    }
    return (
        <>
            <Group label="退款订单信息">
                <SC.FieldList>
                    <SC.FieldItem>
                        <PopoverQuestion
                            type="orderId"
                            renderTarget={(node, ref) => (
                                <Flex gap={16} ref={ref} alignItems="center">
                                    <SC.FieldTitle required>商户退款单号</SC.FieldTitle>
                                    {node}
                                </Flex>
                            )}
                        />
                        <Select
                            value={primaryDsSystemFieldId}
                            options={lookTextFieldOptions}
                            styles={{
                                root: {
                                    width: '100%'
                                }
                            }}
                            disabled
                        />
                    </SC.FieldItem>
                    <SC.FieldItem>
                        <PopoverQuestion
                            type="refundPayOrderId"
                            renderTarget={(node, ref) => (
                                <Flex gap={16} ref={ref} alignItems="center">
                                    <SC.FieldTitle required>微信支付订单号</SC.FieldTitle>
                                    {node}
                                </Flex>
                            )}
                        />

                        <FieldSelect
                            options={lookTextFieldOptions}
                            styles={{
                                root: {
                                    width: '100%'
                                }
                            }}
                            placeholder="从退款订单中选择字段"
                            value={payOrderFieldId}
                            onChange={payOrderFieldId => onChange({ ...value, payOrderFieldId })}
                        />
                    </SC.FieldItem>
                    <SC.FieldItem>
                        <PopoverQuestion
                            type="orderMoney"
                            renderTarget={(node, ref) => (
                                <Flex gap={16} ref={ref} alignItems="center">
                                    <SC.FieldTitle required>原订单金额 （元）</SC.FieldTitle>
                                    {node}
                                </Flex>
                            )}
                        />
                        <FieldSelect
                            options={lookNumberFieldOptions}
                            styles={{
                                root: {
                                    width: '100%'
                                }
                            }}
                            placeholder="从退款订单中选择字段"
                            value={payAmountFieldId}
                            onChange={payAmountFieldId => onChange({ ...value, payAmountFieldId })}
                        />
                    </SC.FieldItem>
                    <SC.FieldItem>
                        <PopoverQuestion
                            type="orderMoney"
                            renderTarget={(node, ref) => (
                                <Flex gap={16} ref={ref} alignItems="center">
                                    <SC.FieldTitle required>退款金额 （元）</SC.FieldTitle>
                                    {node}
                                </Flex>
                            )}
                        />
                        <FieldSelect
                            options={lookNumberFieldOptions}
                            styles={{
                                root: {
                                    width: '100%'
                                }
                            }}
                            placeholder="从退款订单中选择字段"
                            value={refundAmountFieldId}
                            onChange={refundAmountFieldId => onChange({ ...value, refundAmountFieldId })}
                        />
                    </SC.FieldItem>
                    <SC.FieldItem>
                        <Flex gap={16}>
                            <SC.FieldTitle>退款原因</SC.FieldTitle>
                        </Flex>
                        <FieldSelect
                            options={lookTextFieldOptions}
                            styles={{
                                root: {
                                    width: '100%'
                                }
                            }}
                            placeholder="从退款订单中选择字段"
                            value={reasonFieldId}
                            onChange={reasonFieldId => onChange({ ...value, reasonFieldId })}
                        />
                    </SC.FieldItem>
                </SC.FieldList>
            </Group>
            {/* <Divider />
            <Group label="退款成功后更新订单">
                <Box px={8}>
                    <SettingRecordContent
                        prefixName={prefixName}
                        fieldsName="succeedFieldSettings"
                        dataSourceId={dataSource.id}
                        disablePageLink
                        disableFormOption
                        disableViewOption
                        disablePageOption
                    />
                </Box>
            </Group> */}
            <Divider />
            <OutputVariable nodeType={OtherPlatformNodeType.WECHAT_REFUND} />
        </>
    )
}

export default Refund
